// src/Components/Maps/ClockMap.js
import React, { useMemo } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Circle,
  Polygon,
  Popup,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/* ---------- factory for colored marker icons ---------- */
const makeIcon = (url) =>
  new L.Icon({
    iconUrl: url,
    shadowUrl:
      'https://unpkg.com/leaflet@1.9/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

const greenIcon = makeIcon(
  'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png'
);
const redIcon = makeIcon(
  'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png'
);
const greyIcon = makeIcon(
  'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-grey.png'
);

/** format timestamp in Dubai TZ */
const fmtTime = (ts) =>
  new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Dubai',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(ts * 1000));

/**
 * props:
 *   records: Array of { lat, lon, timestamp, event_type }
 *   zones:    Array of mixed:
 *             { id, lat, lon, radius }              // circle
 *             { id, paths:[[lat,lng],...], name }   // polygon
 */
export default function ClockMap({ records = [], zones = [] }) {
  // filter valid record points
  const recs = records
    .filter((r) => r.lat != null && r.lon != null && !isNaN(+r.lat) && !isNaN(+r.lon))
    .map((r) => ({ ...r, position: [+r.lat, +r.lon] }));

  // determine center: first record → first zone → fallback
  const center = useMemo(() => {
    if (recs.length) return recs[0].position;
    if (zones.length) {
      const z = zones[0];
      if (z.lat != null && z.lon != null && !isNaN(+z.lat) && !isNaN(+z.lon)) return [+z.lat, +z.lon];
      if (z.paths?.length) {
        const validPath = z.paths.find(([lat, lng]) => lat != null && lng != null && !isNaN(+lat) && !isNaN(+lng));
        if (validPath) return validPath;
      }
    }
    return [25.2048, 55.2708]; // Dubai
  }, [recs, zones]);

  return (
    <MapContainer
      center={center}
      zoom={15}
      style={{ height: 320, width: '100%', borderRadius: 8 }}
      scrollWheelZoom={false}
      attributionControl={false}
    >
      <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />

      {/* draw zones: circles and polygons */}
      {zones.map((z) => {
        if (z.radius != null && z.lat != null && z.lon != null && !isNaN(+z.lat) && !isNaN(+z.lon)) {
          return (
            <Circle
              key={`c${z.id}`}
              center={[+z.lat, +z.lon]}
              radius={z.radius}
              pathOptions={{
                color: '#4caf50',
                weight: 2,
                fillColor: '#81c784',
                fillOpacity: 0.15,
              }}
            />
          );
        } else if (z.paths?.length) {
          const validPaths = z.paths.filter(([lat, lng]) => lat != null && lng != null && !isNaN(+lat) && !isNaN(+lng));
          if (validPaths.length) {
            return (
              <Polygon
                key={`p${z.id}`}
                positions={validPaths}
                pathOptions={{
                  color: '#2196f3',
                  weight: 2,
                  fillColor: '#64b5f6',
                  fillOpacity: 0.15,
                }}
              />
            );
          }
        }
        return null;
      })}

      {/* draw record markers */}
      {recs.length > 0 && recs.map((r, i) => {
        const icon =
          r.event_type === 'clock_in'
            ? greenIcon
            : r.event_type === 'clock_out'
            ? greyIcon
            : redIcon;
        return (
          <Marker key={i} position={r.position} icon={icon}>
            <Popup>
              <strong>{r.event_type.replace('_', ' ')}</strong>
              <br />
              {fmtTime(r.timestamp)}
            </Popup>
          </Marker>
        );
      })}

      {/* optional polyline connecting all records */}
      {recs.length > 1 && (
        <Polyline positions={recs.map((r) => r.position)} />
      )}
    </MapContainer>
  );
}
